import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  Coordinates,
  SelectedAddressWithCity,
} from '@/types/models/address';
import type { PublicRetrofitOrderSummary } from '@/utils/publicBatteryEcomm/orderSummary';

export type PublicBatteryRetrofitState = {
  selectedQuantity: number | undefined;
  address: SelectedAddressWithCity | undefined;
  coordinates: Coordinates | undefined;
  selectedUtility: string;
  customerEmail: string | undefined;
  orderSummary: PublicRetrofitOrderSummary | undefined;
};

export const initialState: PublicBatteryRetrofitState = {
  selectedQuantity: undefined,
  address: undefined,
  coordinates: undefined,
  selectedUtility: '',
  customerEmail: undefined,
  orderSummary: undefined,
};

export const publicBatteryRetrofitSlice = createSlice({
  name: 'PublicBatteryRetrofit',
  initialState,
  reducers: {
    setSelectedQuantity: (
      state,
      action: PayloadAction<{ selectedQuantity: number }>
    ) => {
      state.selectedQuantity = action.payload.selectedQuantity;
    },
    setAddress: (
      state,
      action: PayloadAction<SelectedAddressWithCity | undefined>
    ) => {
      state.address = action.payload;
    },
    setCoordinates: (state, action: PayloadAction<Coordinates | undefined>) => {
      state.coordinates = action.payload;
    },
    setUtility: (state, action: PayloadAction<{ utility: string }>) => {
      state.selectedUtility = action.payload.utility;
    },
    setCustomerEmail: (state, action: PayloadAction<string | undefined>) => {
      state.customerEmail = action.payload;
    },
    setOrderSummary: (
      state,
      action: PayloadAction<PublicRetrofitOrderSummary | undefined>
    ) => {
      state.orderSummary = action.payload;
    },
    resetPublicBatteryRetrofit: () => {
      return initialState;
    },
  },
  selectors: {
    selectSelectedQuantity: (sliceState) => sliceState.selectedQuantity,
    selectAddress: (sliceState) => sliceState.address,
    selectCoordinates: (sliceState) => sliceState.coordinates,
    selectSelectedUtility: (sliceState) => sliceState.selectedUtility,
    selectCustomerEmail: (sliceState) => sliceState.customerEmail,
    selectOrderSummary: (sliceState) => sliceState.orderSummary,
  },
});

export const {
  setSelectedQuantity,
  setAddress,
  setCoordinates,
  setUtility,
  setCustomerEmail,
  setOrderSummary,
  resetPublicBatteryRetrofit,
} = publicBatteryRetrofitSlice.actions;

export const {
  selectSelectedQuantity,
  selectAddress,
  selectCoordinates,
  selectSelectedUtility,
  selectCustomerEmail,
  selectOrderSummary,
} = publicBatteryRetrofitSlice.selectors;

export default publicBatteryRetrofitSlice.reducer;
export type PublicBatteryRetrofitSlice = typeof publicBatteryRetrofitSlice;
